import type {
  CevroMessengerConfig,
  PlayerData,
  Message,
  QuickAction,
  ThemeConfig,
  WidgetAppearanceConfig,
} from '../types';
import { EventEmitter } from './EventEmitter';
import { ApiClient } from './ApiClient';
import { WebSocketClient } from './WebSocketClient';
import { Launcher } from '../ui/Launcher';
import { ChatWindow } from '../ui/ChatWindow';
import { ConversationList } from '../ui/ConversationList';
import { PastConversation } from '../ui/PastConversation';
import {
  generateVisitorId,
  getStoredSession,
  storeSession,
  clearStoredSession,
  clearStoredVisitorToken,
  getStoredVisitorToken,
  storeVisitorToken,
  recoverVisitorToken,
  detectAndRecordPlayerSwitch,
  clearActivePlayer,
} from '../utils/session';
import { isValidUrl } from '../utils/dom';
import { getStrings } from '../i18n/strings';
import styles from '../styles/index.css?inline';

/**
 * Main Cevro Messenger SDK class
 */
export class CevroMessenger {
  private static instance: CevroMessenger | null = null;

  private config: CevroMessengerConfig;
  private events: EventEmitter;
  private api: ApiClient;
  private ws: WebSocketClient;
  private launcher: Launcher | null = null;
  private chatWindow: ChatWindow | null = null;
  private conversationList: ConversationList | null = null;
  private pastConversation: PastConversation | null = null;
  private currentScreen: 'chat' | 'history' | 'past-conversation' = 'chat';
  private container: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private isBooted = false;
  private isVisible = false;
  private unreadCount = 0;
  private visitorId: string | null;

  private isSessionInitialized = false;
  private isInitializingSession: Promise<void> | null = null;
  private connectingPromise: Promise<void> | null = null;
  private typingCycleTimeout: ReturnType<typeof setTimeout> | null = null;
  private isTypingVisible = false;
  private typingConfig = { onDurationMs: 7000, offDurationMs: 4000 };
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  private visitorContactInfo: { firstName: string; email: string } | null = null;
  private widgetAppearance: WidgetAppearanceConfig = {};
  private customLauncherHandler: (() => void) | null = null;
  private customLauncherElement: Element | null = null;
  private isDestroyed = false;

  constructor(config: CevroMessengerConfig) {
    this.config = this.normalizeConfig(config);
    this.events = new EventEmitter();
    this.api = new ApiClient(this.config);
    this.ws = new WebSocketClient(this.config, this.events);
    this.visitorId = generateVisitorId();

    // Setup token refresh callback for WebSocket
    this.ws.setTokenRefreshCallback(async () => {
      return this.refreshConnectionToken();
    });

    // Setup internal event handlers
    this.setupEventHandlers();

    // Singleton pattern
    CevroMessenger.instance = this;
  }

  /**
   * Get singleton instance
   */
  static getInstance(): CevroMessenger | null {
    return CevroMessenger.instance;
  }

  /**
   * Normalize and validate config
   */
  private normalizeConfig(config: CevroMessengerConfig): CevroMessengerConfig {
    return {
      ...config,
      apiBase: config.apiBase.replace(/\/$/, ''), // Remove trailing slash
      alignment: config.alignment || 'right',
      verticalPadding: config.verticalPadding ?? 20,
      horizontalPadding: config.horizontalPadding ?? 20,
      zIndex: config.zIndex ?? 2147483647,
    };
  }

  /**
   * Setup internal event handlers
   */
  private setupEventHandlers(): void {
    // Handle incoming messages
    this.events.on('message:received', (data: { ticketId: string; message: Message }) => {
      // Stop typing cycle and hide indicator when message arrives
      this.stopTypingCycle();

      if (this.chatWindow) {
        this.chatWindow.addMessage(data.message);
      }

      // Update unread count if window is not visible
      if (!this.isVisible) {
        this.setUnreadCount(this.unreadCount + 1);
      }
    });

    // Handle typing indicators from backend
    this.events.on('typing:start', (data: { ticketId: string; actor: string }) => {
      if (data.ticketId === this.api.getCurrentTicketId()) {
        this.startTypingCycle();
      }
    });

    this.events.on('typing:stop', () => {
      this.stopTypingCycle();
    });

    // Handle conversation/ticket status updates (real-time close from admin)
    this.events.on('conversation:updated', (data: { ticketId: string; status: string; message?: string; agentName?: string }) => {
      // Ignore events for tickets that aren't the current one
      if (data.ticketId !== this.api.getCurrentTicketId()) return;

      if (data.status === 'escalated') {
        // Show system message that a human agent joined
        const agentName = data.agentName || 'Support';
        const escalationMsg = data.message || `${agentName} has joined the conversation.`;
        this.chatWindow?.addMessage({
          id: `escalation-${Date.now()}`,
          body: escalationMsg,
          inbound: false,
          outbound: true,
          createdAt: new Date().toISOString(),
          type: 'text',
          senderType: 'human',
          agentName,
        });
      }

      if (data.status === 'closed') {
        const skipCSAT = this.widgetAppearance.csatEnabled !== true;
        this.chatWindow?.setTicketClosed(true, () => this.startNewChat(), {
          ticketId: data.ticketId,
          skipCSAT,
        });
      }
    });
  }

  /**
   * Boot the messenger
   */
  async boot(): Promise<void> {
    if (this.isBooted) {
      return;
    }

    this.isDestroyed = false;

    // Recover visitor identity from cookie/IndexedDB if localStorage was cleared
    if (!this.config.preview) {
      const recovered = await recoverVisitorToken();
      if (recovered) {
        this.api.setVisitorToken(recovered);
      }
    }

    // Fetch widget config from backend (non-blocking for UI)
    if (!this.config.preview) {
      try {
        this.widgetAppearance = await this.api.getWidgetConfig();
      } catch {
        this.widgetAppearance = {};
      }
    }

    // Guard again after async — another boot() or shutdown() may have run
    if (this.isBooted || this.isDestroyed) {
      return;
    }

    // Create container with Shadow DOM for style isolation
    const host = document.createElement('div');
    host.id = 'cevro-messenger-host';
    document.body.appendChild(host);

    // Create Shadow DOM
    this.shadowRoot = host.attachShadow({ mode: 'open' });

    // Inject styles into Shadow DOM
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    this.shadowRoot.appendChild(styleEl);

    // Create inner container for CSS variables
    this.container = document.createElement('div');
    this.container.id = 'cevro-messenger-container';
    this.shadowRoot.appendChild(this.container);

    this.applyPrimaryColor();

    // Create launcher (skip if hideDefaultLauncher is set)
    if (!this.config.hideDefaultLauncher) {
      this.createAndMountLauncher();
    }

    // Create chat window with merged locale strings
    const strings = getStrings(this.config.locale);
    this.chatWindow = new ChatWindow(
      this.config,
      {
        onClose: () => this.hide(),
        onSendMessage: (message) => this.sendMessage(message),
        onQuickAction: (action) => this.handleQuickAction(action),
        onFileUpload: (file) => this.handleFileUpload(file),
        onCSATSubmit: async (rating, feedback) => this.submitCSAT(rating, feedback),
        onStartNewChat: () => this.startNewChat(),
        onPreChatSubmit: (data) => this.handlePreChatSubmit(data),
        onShowHistory: () => this.navigateTo('history'),
      },
      strings,
      this.widgetAppearance.customLogoUrl
    );
    this.chatWindow.setBrandLogoUrl(this.firstMessageBrandLogoUrl());
    this.container.appendChild(this.chatWindow.render());

    // Create conversation history screens
    this.createConversationList(strings);
    this.createPastConversation(strings);

    // Check for existing session (will be properly restored on show())
    const storedSession = getStoredSession();
    if (storedSession?.ticketId) {
      this.api.setCurrentTicketId(storedSession.ticketId);
    }

    // Bind custom launcher element if selector provided
    if (this.config.customLauncherSelector) {
      this.bindCustomLauncher(this.config.customLauncherSelector);
    }

    this.isBooted = true;
    this.events.emit('boot');

    if (this.widgetAppearance.autoOpen) {
      setTimeout(() => this.show(), 100);
    }
  }

  private applyPrimaryColor(): void {
    const color = this.widgetAppearance.primaryColor || this.config.primaryColor;
    if (color && this.container) {
      // Set CSS custom property on container
      this.container.style.setProperty('--cevro-primary', color);

      // Generate hover (darker) and light variants
      const hoverColor = this.adjustColorBrightness(color, -20);
      const lightColor = this.adjustColorBrightness(color, 40);

      this.container.style.setProperty('--cevro-primary-hover', hoverColor);
      this.container.style.setProperty('--cevro-primary-light', lightColor);
    }

    // Apply theme (from config or backend)
    this.applyTheme();
  }

  /**
   * Apply theme configuration
   * Backend theme is applied first, then local config overrides
   */
  private applyTheme(): void {
    if (!this.container) return;

    const backendTheme = this.widgetAppearance.theme;
    const localTheme = this.config.theme;

    if (!backendTheme && !localTheme) return;

    const mergedTheme: ThemeConfig = {
      ...backendTheme,
      ...localTheme,
    };

    this.applyCustomTheme(mergedTheme);
  }

  /**
   * Apply custom theme configuration
   */
  private applyCustomTheme(theme: ThemeConfig): void {
    if (!this.container) return;

    // Remove all theme CSS vars first so unset properties fall back to CSS defaults
    const themeVars = [
      '--cevro-bg', '--cevro-bg-secondary', '--cevro-text', '--cevro-text-secondary',
      '--cevro-border', '--cevro-user-msg-bg', '--cevro-user-msg-text',
      '--cevro-header-bg', '--cevro-header-text', '--cevro-bg-input',
      '--cevro-radius', '--cevro-radius-sm', '--cevro-shadow', '--cevro-shadow-sm',
      '--cevro-launcher-bg', '--cevro-launcher-icon', '--cevro-launcher-hover-shadow',
      '--cevro-send-btn-bg', '--cevro-send-btn-icon',
      '--cevro-file-btn-bg', '--cevro-file-btn-icon',
      '--cevro-agent-msg-bg', '--cevro-agent-msg-text',
    ];
    for (const v of themeVars) {
      this.container.style.removeProperty(v);
    }

    if (theme.backgroundColor) {
      this.container.style.setProperty('--cevro-bg', theme.backgroundColor);
    }
    if (theme.backgroundSecondaryColor) {
      this.container.style.setProperty('--cevro-bg-secondary', theme.backgroundSecondaryColor);
    }
    if (theme.textColor) {
      this.container.style.setProperty('--cevro-text', theme.textColor);
    }
    if (theme.textSecondaryColor) {
      this.container.style.setProperty('--cevro-text-secondary', theme.textSecondaryColor);
    }
    if (theme.borderColor) {
      this.container.style.setProperty('--cevro-border', theme.borderColor);
    }
    if (theme.userMessageBackgroundColor) {
      this.container.style.setProperty('--cevro-user-msg-bg', theme.userMessageBackgroundColor);
    }
    if (theme.userMessageTextColor) {
      this.container.style.setProperty('--cevro-user-msg-text', theme.userMessageTextColor);
    }
    if (theme.headerBackgroundColor) {
      this.container.style.setProperty('--cevro-header-bg', theme.headerBackgroundColor);
    }
    if (theme.headerTextColor) {
      this.container.style.setProperty('--cevro-header-text', theme.headerTextColor);
    }
    if (theme.inputBackgroundColor) {
      this.container.style.setProperty('--cevro-bg-input', theme.inputBackgroundColor);
    }
    if (theme.borderRadius !== undefined) {
      this.container.style.setProperty('--cevro-radius', `${theme.borderRadius}px`);
      this.container.style.setProperty('--cevro-radius-sm', `${Math.max(4, theme.borderRadius - 4)}px`);
    }
    if (theme.shadowColor) {
      this.container.style.setProperty('--cevro-shadow', `0 10px 40px ${theme.shadowColor}`);
      this.container.style.setProperty('--cevro-shadow-sm', `0 2px 8px ${theme.shadowColor}`);
    }
    // Launcher styling
    if (theme.launcherBackgroundColor) {
      this.container.style.setProperty('--cevro-launcher-bg', theme.launcherBackgroundColor);
    }
    if (theme.launcherIconColor) {
      this.container.style.setProperty('--cevro-launcher-icon', theme.launcherIconColor);
    }
    if (theme.launcherHoverShadowColor) {
      this.container.style.setProperty('--cevro-launcher-hover-shadow', theme.launcherHoverShadowColor);
    }
    // Send button styling
    if (theme.sendButtonBackgroundColor) {
      this.container.style.setProperty('--cevro-send-btn-bg', theme.sendButtonBackgroundColor);
    }
    if (theme.sendButtonIconColor) {
      this.container.style.setProperty('--cevro-send-btn-icon', theme.sendButtonIconColor);
    }
    // File button styling
    if (theme.fileButtonBackgroundColor) {
      this.container.style.setProperty('--cevro-file-btn-bg', theme.fileButtonBackgroundColor);
    }
    if (theme.fileButtonIconColor) {
      this.container.style.setProperty('--cevro-file-btn-icon', theme.fileButtonIconColor);
    }
    // Agent message styling
    if (theme.agentMessageBackgroundColor) {
      this.container.style.setProperty('--cevro-agent-msg-bg', theme.agentMessageBackgroundColor);
    }
    if (theme.agentMessageTextColor) {
      this.container.style.setProperty('--cevro-agent-msg-text', theme.agentMessageTextColor);
    }
  }

  /**
   * Adjust color brightness
   * @param hex - Hex color string
   * @param percent - Positive to lighten, negative to darken
   */
  private adjustColorBrightness(hex: string, percent: number): string {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse RGB
    const num = parseInt(hex, 16);
    let r = (num >> 16) + percent;
    let g = ((num >> 8) & 0x00ff) + percent;
    let b = (num & 0x0000ff) + percent;

    // Clamp values
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }

  /**
   * Create and mount the launcher into the container
   */
  private createAndMountLauncher(): void {
    if (!this.container) return;
    this.launcher = new Launcher(
      this.config,
      () => this.toggle(),
      this.widgetAppearance.bubbleIconUrl
    );
    this.container.appendChild(this.launcher.render());
  }

  /**
   * Create and mount conversation list screen
   */
  private createConversationList(strings: ReturnType<typeof getStrings>): void {
    if (!this.container) return;

    this.conversationList = new ConversationList(
      {
        onClose: () => this.hide(),
        onNewConversation: async () => {
          this.navigateTo('chat');
          await this.startNewChat();
        },
        onSelectActive: () => {
          this.navigateTo('chat');
        },
        onSelectPast: (ticketId, title) => {
          this.navigateTo('past-conversation');
          this.pastConversation?.loadConversation(ticketId, title);
        },
        onLoadConversations: (cursor) => this.api.getConversations(cursor),
      },
      strings
    );

    const el = this.conversationList.render();
    el.style.display = 'none';
    this.container.appendChild(el);
  }

  /**
   * Create and mount past conversation view
   */
  private createPastConversation(strings: ReturnType<typeof getStrings>): void {
    if (!this.container) return;

    this.pastConversation = new PastConversation(
      {
        onBack: () => this.navigateTo('history'),
        onClose: () => this.hide(),
        onNewConversation: async () => {
          this.navigateTo('chat');
          await this.startNewChat();
        },
        onLoadMessages: (ticketId, cursor) => this.api.getConversationMessages(ticketId, cursor),
      },
      strings
    );

    const el = this.pastConversation.render();
    this.pastConversation.setAvatarUrl(this.widgetAppearance.customLogoUrl);
    this.pastConversation.setBrandLogoUrl(this.firstMessageBrandLogoUrl());
    el.style.display = 'none';
    this.container.appendChild(el);
  }

  /**
   * Navigate between screens
   */
  private navigateTo(screen: 'chat' | 'history' | 'past-conversation'): void {
    this.currentScreen = screen;

    // Hide all screens
    this.chatWindow?.hide();
    this.conversationList?.hide();
    this.pastConversation?.hide();

    // Show target screen
    switch (screen) {
      case 'chat':
        this.chatWindow?.show();
        break;
      case 'history':
        if (this.isSessionInitialized && this.api.getCurrentTicketId()) {
          this.conversationList?.setActiveConversation({
            ticketId: this.api.getCurrentTicketId()!,
            lastMessageBody: this.chatWindow?.getLastMessageBody(),
            lastMessageAt: this.chatWindow?.getLastMessageAt(),
            agentName: this.api.getCurrentAgent()?.name,
            unreadCount: this.chatWindow?.getAgentMessagesSinceLastUserMessage() ?? 0,
          });
        } else {
          this.conversationList?.setActiveConversation(null);
        }
        this.conversationList?.show();
        this.conversationList?.load();
        break;
      case 'past-conversation':
        this.pastConversation?.show();
        break;
    }
  }

  /**
   * Bind a custom launcher element by CSS selector
   */
  private bindCustomLauncher(selector: string): void {
    const el = document.querySelector(selector);
    if (!el) {
      console.warn(`[CevroMessenger] customLauncherSelector "${selector}" did not match any element`);
      return;
    }

    this.customLauncherHandler = () => this.toggle();
    this.customLauncherElement = el;
    el.addEventListener('click', this.customLauncherHandler);
  }

  /**
   * Connect to Centrifugo WebSocket server
   * Fetches connection token from API first
   *
   * Security: Uses signed visitorToken to prevent visitor spoofing
   */
  private async connectWebSocket(): Promise<void> {
    // If already connecting, wait for existing promise
    if (this.connectingPromise) {
      return this.connectingPromise;
    }

    this.connectingPromise = this.doConnectWebSocket();
    return this.connectingPromise;
  }

  private async doConnectWebSocket(): Promise<void> {
    try {
      const storedVisitorToken = getStoredVisitorToken();
      const result = await this.api.getConnectionToken(storedVisitorToken);

      this.visitorId = result.visitorId;

      if (result.visitorToken) {
        storeVisitorToken(result.visitorToken);
        this.api.setVisitorToken(result.visitorToken);
      }

      this.ws.connect(result.visitorId, result.token);
      this.events.emit('ready');
    } catch (error) {
      // Still emit ready - SDK works without real-time, just with polling
      this.events.emit('ready');
      this.events.emit('error', { type: 'websocket_connect', error });
    } finally {
      this.connectingPromise = null;
    }
  }

  /**
   * Refresh connection token for Centrifugo
   * Called automatically when token expires or on reconnect
   */
  private async refreshConnectionToken(): Promise<string> {
    const storedVisitorToken = getStoredVisitorToken();
    const result = await this.api.getConnectionToken(storedVisitorToken);

    if (result.visitorToken) {
      storeVisitorToken(result.visitorToken);
      this.api.setVisitorToken(result.visitorToken);
    }

    return result.token;
  }

  /**
   * Shutdown the messenger
   * @param options.clearVisitorData - If true, clears visitor token (visitor will get new identity on next init)
   */
  shutdown(options?: { clearVisitorData?: boolean }): void {
    this.isDestroyed = true;

    if (!this.isBooted) {
      // Even pre-boot, clear stored data if requested — the caller
      // explicitly wants a clean slate (e.g. logout flow).
      if (options?.clearVisitorData) {
        clearStoredSession();
        clearStoredVisitorToken();
        clearActivePlayer();
        this.visitorId = null;
      }
      return;
    }

    // Stop typing cycle
    this.stopTypingCycle();

    // Disconnect WebSocket
    this.ws.disconnect();

    // Unbind custom launcher
    if (this.customLauncherElement && this.customLauncherHandler) {
      this.customLauncherElement.removeEventListener('click', this.customLauncherHandler);
      this.customLauncherElement = null;
      this.customLauncherHandler = null;
    }

    // Destroy UI components
    this.launcher?.destroy();
    this.chatWindow?.destroy();
    this.conversationList?.destroy();
    this.pastConversation?.destroy();

    // Remove Shadow DOM host element
    const host = this.shadowRoot?.host;
    if (host && host.parentNode) {
      host.parentNode.removeChild(host);
    }
    this.shadowRoot = null;

    // Clear stored data
    clearStoredSession();
    if (options?.clearVisitorData) {
      clearStoredVisitorToken();
      clearActivePlayer();
      this.visitorId = null;
    }
    this.api.clear();

    // Reset state
    this.launcher = null;
    this.chatWindow = null;
    this.conversationList = null;
    this.pastConversation = null;
    this.currentScreen = 'chat';
    this.container = null;
    this.isBooted = false;
    this.isVisible = false;
    this.unreadCount = 0;
    this.isSessionInitialized = false;
    this.isInitializingSession = null;
    this.connectingPromise = null;

    this.events.emit('shutdown');
  }

  /**
   * Show the messenger
   */
  async show(): Promise<void> {
    if (!this.isBooted) {
      await this.boot();
    }

    if (this.chatWindow) {
      // Show the current screen (default: chat)
      this.navigateTo(this.currentScreen);
      this.isVisible = true;
      this.launcher?.setState(true);
      this.setUnreadCount(0);
      this.events.emit('show');

      // Close on Escape key (desktop only)
      if (!this.escapeHandler) {
        this.escapeHandler = (e: KeyboardEvent) => {
          if (e.key === 'Escape') this.hide();
        };
        document.addEventListener('keydown', this.escapeHandler);
      }

      // In preview mode, skip WebSocket and session initialization
      if (!this.config.preview) {
        await this.connectWebSocket();

        // Set visitor ID on API client for history requests
        if (this.visitorId) {
          this.api.setVisitorId(this.visitorId);
        }

        await this.initializeSession();
      }
    }
  }

  /**
   * Initialize session with backend
   * Creates/retrieves Contact and Ticket, loads existing messages
   * Uses promise pattern to ensure proper waiting when called concurrently
   */
  private async initializeSession(forceNew = false): Promise<void> {
    // If forcing new session, reset state
    if (forceNew) {
      this.isSessionInitialized = false;
      this.isInitializingSession = null;
    }

    // Already initialized - nothing to do
    if (this.isSessionInitialized) {
      return;
    }

    // If initialization is in progress, wait for it to complete
    if (this.isInitializingSession) {
      return this.isInitializingSession;
    }

    // Start new initialization - wrap with finally to ensure cleanup
    const initPromise = this.doInitializeSession().finally(() => {
      // Only clear if this is still the current initialization promise
      if (this.isInitializingSession === initPromise) {
        this.isInitializingSession = null;
      }
    });

    this.isInitializingSession = initPromise;
    return initPromise;
  }

  /**
   * Actual session initialization logic
   */
  private async doInitializeSession(options?: { forceNewTicket?: boolean }): Promise<void> {
    try {
      const result = await this.api.initSession(this.visitorId, this.visitorContactInfo, options);

      // Check if contact info is required but missing
      if (result.requireContactInfo && result.missingFields && result.missingFields.length > 0) {
        this.chatWindow?.showPreChatForm();
        return;
      }

      // Store session data
      storeSession(result.sessionId, result.contactId, result.ticketId);
      this.isSessionInitialized = true;

      // Set agent info in header
      if (result.agent && this.chatWindow) {
        this.chatWindow.setAgent(result.agent);
      }
      if (result.agent && this.pastConversation) {
        this.pastConversation.setAgent(result.agent);
      }

      // Store typing config from server
      if (result.typingConfig) {
        this.typingConfig = result.typingConfig;
      }

      // Apply backend locale strings (highest priority: backend > client config > defaults)
      if (result.locale && this.chatWindow) {
        const mergedStrings = getStrings(this.config.locale, result.locale);
        this.chatWindow.setStrings(mergedStrings);
        this.conversationList?.setStrings(mergedStrings);
        this.pastConversation?.setStrings(mergedStrings);
      }

      this.chatWindow?.setScrollTopHandler(null);

      // Load existing messages into chat window
      if (result.messages && result.messages.length > 0) {
        for (const message of result.messages) {
          this.chatWindow?.addMessage(message);
        }

        // Enable scroll-up loading for older messages.
        // Do not depend solely on /session hasMoreMessages, because some backends
        // return only the latest slice here and expose pagination only via
        // /conversations/:ticketId/messages.
        if (result.ticketId) {
          let olderMessagesCursor: string | undefined =
            result.cursor || result.messages[0]?.createdAt;
          let isLoadingOlder = false;

          this.chatWindow?.setScrollTopHandler(async () => {
            if (isLoadingOlder || !olderMessagesCursor) return;
            isLoadingOlder = true;
            try {
              const older = await this.api.getConversationMessages(
                result.ticketId,
                olderMessagesCursor,
              );
              this.chatWindow?.prependMessages(older.messages);
              olderMessagesCursor = older.hasMore
                ? (older.cursor || older.messages[0]?.createdAt)
                : undefined;
            } catch (error) {
              console.error('[Cevro] Failed to load older messages:', error);
            } finally {
              isLoadingOlder = false;
              this.chatWindow?.finishLoadingTop();
            }
          });
        }
      }

      // Handle closed ticket state
      if (result.ticketClosed) {
        const skipCSAT = this.widgetAppearance.csatEnabled !== true;
        this.chatWindow?.setTicketClosed(true, () => this.startNewChat(), {
          ticketId: result.ticketId,
          skipCSAT,
        });
      }

      this.events.emit('session:initialized', {
        sessionId: result.sessionId,
        contactId: result.contactId,
        ticketId: result.ticketId,
        isNewSession: result.isNewSession,
        isNewTicket: result.isNewTicket,
        ticketClosed: result.ticketClosed,
        messageCount: result.messages?.length || 0,
      });

    } catch (error) {
      this.events.emit('error', { type: 'session_init', error });
      throw error;
    }
  }

  /**
   * Submit CSAT rating for the current conversation
   */
  private async submitCSAT(rating: number, feedback?: string): Promise<void> {
    const ticketId = this.api.getCurrentTicketId();
    const contactId = this.api.getContactId();

    if (!ticketId || !contactId) {
      throw new Error('No active session');
    }

    await this.api.submitCSAT(ticketId, contactId, rating, feedback);
  }

  /**
   * Start a new chat (creates a new ticket)
   * Used when the previous chat was closed
   */
  async startNewChat(): Promise<void> {
    // Navigate to chat screen first
    this.navigateTo('chat');

    // Clear current chat window messages
    this.chatWindow?.clearMessages();
    this.chatWindow?.setTicketClosed(false);

    // Re-initialize session with forceNewTicket
    this.isSessionInitialized = false;
    await this.doInitializeSession({ forceNewTicket: true });

    this.events.emit('chat:new_started');
  }

  /**
   * Hide the messenger
   */
  hide(): void {
    if (this.chatWindow) {
      this.chatWindow.hide();
      this.conversationList?.hide();
      this.pastConversation?.hide();
      this.isVisible = false;
      this.launcher?.setState(false);
      this.events.emit('hide');

      if (this.escapeHandler) {
        document.removeEventListener('keydown', this.escapeHandler);
        this.escapeHandler = null;
      }
    }
  }

  /**
   * Show the default launcher bubble
   */
  showLauncher(): void {
    if (!this.isBooted) return;
    if (!this.launcher) {
      this.createAndMountLauncher();
    }
    this.launcher?.setVisible(true);
  }

  /**
   * Hide the default launcher bubble (widget remains functional via show())
   */
  hideLauncher(): void {
    if (!this.isBooted) return;
    this.launcher?.setVisible(false);
  }

  /**
   * Toggle messenger visibility
   */
  toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Maximum message length (2000 characters)
   */
  private static readonly MAX_MESSAGE_LENGTH = 2000;

  /**
   * Set draft message in the textarea without sending
   */
  setDraft(message: string): void {
    this.chatWindow?.setDraft(message);
  }

  /**
   * Send a message
   */
  async sendMessage(message: string): Promise<void> {
    if (!message.trim()) return;

    // Validate message length
    if (message.length > CevroMessenger.MAX_MESSAGE_LENGTH) {
      this.events.emit('error', {
        type: 'validation',
        error: new Error(`Message is too long. Maximum ${CevroMessenger.MAX_MESSAGE_LENGTH} characters allowed.`),
        message: `Message is too long. Maximum ${CevroMessenger.MAX_MESSAGE_LENGTH} characters allowed.`,
      });
      return;
    }

    // Create optimistic message
    // inbound: true = user's message going TO backend (consistent with backend storage)
    const optimisticMessage: Message = {
      id: 'temp_' + Date.now(),
      body: message,
      inbound: true,
      outbound: false,
      createdAt: new Date().toISOString(),
      type: 'text',
      status: 'sending',
    };

    // Add to UI immediately
    this.chatWindow?.addMessage(optimisticMessage);

    // In preview mode, just show the message without API call
    if (this.config.preview) {
      optimisticMessage.status = 'sent';
      this.chatWindow?.updateMessage(optimisticMessage);
      return;
    }

    try {
      // Ensure session is initialized before sending
      if (!this.isSessionInitialized) {
        await this.initializeSession();
      }

      // Store original temp id for lookup
      const tempId = optimisticMessage.id;

      // Send message to existing conversation
      const result = await this.api.sendMessage(message);

      // Update optimistic message with real data
      optimisticMessage.id = result.id;
      optimisticMessage.status = 'sent';
      this.chatWindow?.updateMessage(optimisticMessage, tempId);

      this.events.emit('message:sent', { message: result });

      // Typing indicator will be shown by backend via WebSocket event

    } catch (error: any) {
      // Check if conversation is closed
      const isConversationClosed =
        error?.message?.includes('closed') ||
        error?.message?.includes('resolved') ||
        error?.message?.includes('escalated') ||
        error?.response?.data?.message?.includes('closed');

      if (isConversationClosed) {
        // Remove the failed optimistic message
        this.chatWindow?.removeMessage(optimisticMessage.id);

        // Show closed UI with "New Conversation" button
        const ticketId = this.api.getCurrentTicketId() || undefined;
        this.chatWindow?.setTicketClosed(true, () => this.startNewChat(), {
          ticketId,
          skipCSAT: this.widgetAppearance.csatEnabled !== true,
        });
        return;
      }

      // Update optimistic message with error status
      optimisticMessage.status = 'failed';
      this.chatWindow?.updateMessage(optimisticMessage);

      this.events.emit('error', { error, type: 'send_message' });
    }
  }

  /**
   * Handle quick action click
   */
  private async handleQuickAction(action: QuickAction): Promise<void> {
    if (action.type === 'link' && action.url) {
      // Validate URL to prevent XSS via javascript: protocol
      if (isValidUrl(action.url)) {
        window.open(action.url, '_blank');
      }
    } else {
      // Send the action value as a message
      await this.sendMessage(action.value || action.label);
    }
  }

  /**
   * Handle pre-chat form submission (for visitors)
   */
  private async handlePreChatSubmit(data: { firstName: string; email: string }): Promise<void> {
    this.visitorContactInfo = data;

    // Hide the form and re-initialize session with contact info
    this.chatWindow?.hidePreChatForm();
    await this.initializeSession();
  }

  /**
   * Handle file upload with progress tracking
   * Shows preview immediately, then uploads to S3, then sends to backend
   *
   * Security: File is validated before upload (size, type, extension)
   */
  private async handleFileUpload(file: File): Promise<void> {
    // Variables for cleanup on error
    let localPreviewUrl: string | undefined;
    let tempId: string | undefined;

    try {
      // Ensure session is initialized before uploading
      if (!this.isSessionInitialized) {
        await this.initializeSession();
      }

      // Determine file type for display
      const isImage = file.type.startsWith('image/');
      const fileCategory = this.getFileCategory(file.type);

      // Create local preview URL for images
      localPreviewUrl = isImage ? URL.createObjectURL(file) : undefined;

      // Create optimistic message IMMEDIATELY (before upload starts)
      tempId = 'temp_' + Date.now();
      const optimisticMessage: Message = {
        id: tempId,
        body: '',
        inbound: true,
        outbound: false,
        createdAt: new Date().toISOString(),
        type: isImage ? 'image' : 'file',
        mediaUrl: localPreviewUrl,
        mediaType: file.type,
        status: 'sending',
        attachment: {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          category: fileCategory,
        },
      };

      // Show preview in chat immediately
      this.chatWindow?.addMessage(optimisticMessage);

      // Now start the upload with progress (validation happens inside uploadFile)
      const assetId = await this.api.uploadFile(file, (progress) => {
        // Update progress in the message itself
        this.chatWindow?.updateMessageProgress(tempId!, progress);
      });

      // Send message to backend
      const result = await this.api.sendMessage('', assetId);

      // Update with real data from server, keeping blob URL if server didn't return mediaUrl
      const serverHasMediaUrl = result.mediaUrl && isValidUrl(result.mediaUrl);

      const updatedMessage: Message = {
        ...result,
        status: 'sent',
        type: isImage ? 'image' : 'file',
        mediaUrl: serverHasMediaUrl ? result.mediaUrl : localPreviewUrl,
        attachment: optimisticMessage.attachment,
      };
      this.chatWindow?.updateMessage(updatedMessage, tempId);

      // Only revoke blob URL if server returned a real URL
      if (localPreviewUrl && serverHasMediaUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }

      this.events.emit('message:sent', { type: fileCategory, fileName: file.name });

    } catch (error: any) {
      // Clean up blob URL on error
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }

      // Remove failed optimistic message from UI
      if (tempId) {
        this.chatWindow?.removeMessage(tempId);
      }

      // Determine error type for better UX
      const errorMessage = error?.message || 'File upload failed';
      const isValidationError =
        errorMessage.includes('too large') ||
        errorMessage.includes('too small') ||
        errorMessage.includes('not allowed') ||
        errorMessage.includes('Too many uploads');

      // Emit specific error event with details
      this.events.emit('error', {
        error,
        type: isValidationError ? 'file_validation' : 'file_upload',
        message: errorMessage,
        fileName: file.name,
      });
    }
  }

  /**
   * Get file category for display purposes
   */
  private getFileCategory(mimeType: string): 'image' | 'pdf' | 'document' | 'spreadsheet' | 'presentation' | 'video' | 'audio' | 'archive' | 'text' | 'file' {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'spreadsheet';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) return 'archive';
    if (mimeType.startsWith('text/')) return 'text';
    return 'file';
  }

  /**
   * Set unread count
   */
  private setUnreadCount(count: number): void {
    this.unreadCount = count;
    this.launcher?.setUnreadCount(count);
    this.events.emit('unread_count_change', { count });
  }

  /**
   * The brand logo pairs with the global, workspace-voiced welcome
   * (`instantGreeting`) — pairing it with the personalized "Hi, I'm
   * {agentName}" welcome would put a company avatar next to agent-attributed
   * text.
   */
  private firstMessageBrandLogoUrl(): string | undefined {
    return this.widgetAppearance.instantGreeting
      ? this.widgetAppearance.customLogoUrl
      : undefined;
  }

  updateAppearance(config: Partial<WidgetAppearanceConfig>): void {
    this.widgetAppearance = {
      ...this.widgetAppearance,
      ...config,
    };

    if ('primaryColor' in config) {
      this.applyPrimaryColor();
    }

    if ('theme' in config) {
      this.applyTheme();
    }

    if ('bubbleIconUrl' in config && this.launcher) {
      this.launcher.updateBubbleIcon(config.bubbleIconUrl);
    }

    // Update chat window logo if changed (check if key exists in config)
    if ('customLogoUrl' in config && this.chatWindow) {
      this.chatWindow.updateCustomLogo(config.customLogoUrl);
    }
    if (('customLogoUrl' in config || 'instantGreeting' in config) && this.chatWindow) {
      this.chatWindow.setBrandLogoUrl(this.firstMessageBrandLogoUrl());
    }
    if (('customLogoUrl' in config || 'instantGreeting' in config) && this.pastConversation) {
      this.pastConversation.setBrandLogoUrl(this.firstMessageBrandLogoUrl());
    }

    this.events.emit('appearance:updated', this.widgetAppearance);
  }

  /**
   * Get current appearance config
   */
  getAppearance(): WidgetAppearanceConfig {
    return { ...this.widgetAppearance };
  }

  /**
   * Subscribe to an event
   * @param event - Event type to listen for
   * @param handler - Callback function
   * @returns Unsubscribe function
   */
  on<T = any>(event: string, handler: (data: T) => void): () => void {
    return this.events.on(event as any, handler);
  }

  /**
   * Subscribe to an event (fires only once)
   */
  once<T = any>(event: string, handler: (data: T) => void): () => void {
    return this.events.once(event as any, handler);
  }

  /**
   * Unsubscribe from an event
   * @param event - Event type
   * @param handler - Handler to remove
   */
  off(event: string, handler: (data: any) => void): void {
    this.events.off(event as any, handler);
  }

  /**
   * Get current unread message count
   */
  getUnreadCount(): number {
    return this.unreadCount;
  }

  /**
   * Get current visitor ID
   */
  getVisitorId(): string | null {
    return this.visitorId;
  }

  /**
   * Update config with new data (merges into current config)
   * Used by the loader queue replay pattern: Cevro('update', { ... })
   */
  update(data: Partial<CevroMessengerConfig>): void {
    this.config = this.normalizeConfig({ ...this.config, ...data });
    // Propagate new config reference to ApiClient and WebSocketClient
    this.api.updateConfig(this.config);
    this.ws.updateConfig(this.config);
  }

  /**
   * Identify a player (sets player data on config)
   * Used by the loader queue replay pattern: Cevro('identify', { playerId: '...' })
   *
   * Performs a soft re-initialization: keeps the UI intact, only re-creates
   * the session (new token + new session with updated player data).
   * No DOM destruction — no flickering.
   */
  async identify(playerData: PlayerData): Promise<void> {
    this.config.player = { ...this.config.player, ...playerData };

    // Detect account switches. First identify() stores the player hash and
    // returns false (anonymous session is preserved for server-side merge).
    // Subsequent identify() with a DIFFERENT playerId returns true — clear
    // stale session data so the new identity doesn't see the old conversation.
    const isSwitch = playerData.playerId
      ? detectAndRecordPlayerSwitch(playerData.playerId)
      : false;

    if (isSwitch) {
      clearStoredSession();
      clearStoredVisitorToken();
    }

    // Propagate updated config to ApiClient and WebSocketClient
    this.api.updateConfig(this.config);
    this.ws.updateConfig(this.config);

    // If not booted yet, just store the data — boot() will use it.
    // On first identify: existing session/visitor data preserved (anonymous→identified merge).
    // On account switch: data was cleared above, boot() starts fresh.
    if (!this.isBooted) {
      return;
    }

    // Soft re-init: disconnect WS, clear session state, reconnect with new identity
    const wasVisible = this.isVisible;
    this.ws.disconnect();
    this.api.clear();
    clearStoredSession();
    this.isSessionInitialized = false;
    this.isInitializingSession = null;
    this.connectingPromise = null;

    try {
      // Reconnect and re-initialize session with new player data
      await this.connectWebSocket();

      // Clear old messages before loading new session's messages
      this.chatWindow?.clearMessages();

      await this.initializeSession(true);

      // Re-apply visibility (session init may have reset UI state)
      if (wasVisible && this.chatWindow) {
        this.chatWindow.show();
      }

      this.events.emit('identify', { playerId: playerData.playerId });
    } catch (error) {
      this.events.emit('error', { type: 'identify', error });
    }
  }

  /**
   * Add a preview message (only works in preview mode)
   * @param text - Message text
   * @param fromAgent - If true, message appears as from agent; if false, from user
   */
  addPreviewMessage(text: string, fromAgent = true): void {
    if (!this.config.preview || !this.chatWindow) return;

    const message: Message = {
      id: 'preview_' + Date.now(),
      body: text,
      inbound: !fromAgent,
      outbound: fromAgent,
      createdAt: new Date().toISOString(),
      type: 'text',
      status: 'sent',
    };

    this.chatWindow.addMessage(message);
  }

  /**
   * Show or hide typing indicator (only works in preview mode)
   * @param show - Whether to show or hide the typing indicator
   */
  setPreviewTyping(show: boolean): void {
    if (!this.config.preview || !this.chatWindow) return;

    if (show) {
      this.chatWindow.showTypingIndicator();
    } else {
      this.chatWindow.hideTypingIndicator();
    }
  }

  /**
   * Set agent info for preview mode (name and avatar)
   * @param name - Agent name
   * @param avatarUrl - Agent avatar URL (optional)
   */
  setPreviewAgent(name: string, avatarUrl?: string): void {
    if (!this.config.preview || !this.chatWindow) return;

    this.chatWindow.setAgent({ name, avatarUrl });
    this.pastConversation?.setAgent({ name, avatarUrl });
  }

  /**
   * Start typing indicator cycle (5s on, 5s off, repeat)
   */
  private startTypingCycle(): void {
    // Clear any existing cycle
    this.stopTypingCycle();

    const { onDurationMs, offDurationMs } = this.typingConfig;

    const runCycle = () => {
      if (this.isTypingVisible) {
        this.chatWindow?.hideTypingIndicator();
        this.isTypingVisible = false;
        this.typingCycleTimeout = setTimeout(runCycle, offDurationMs);
      } else {
        this.chatWindow?.showTypingIndicator();
        this.isTypingVisible = true;
        this.typingCycleTimeout = setTimeout(runCycle, onDurationMs);
      }
    };

    this.chatWindow?.showTypingIndicator();
    this.isTypingVisible = true;
    this.typingCycleTimeout = setTimeout(runCycle, onDurationMs);
  }

  /**
   * Stop typing indicator cycle
   */
  private stopTypingCycle(): void {
    if (this.typingCycleTimeout) {
      clearTimeout(this.typingCycleTimeout);
      this.typingCycleTimeout = null;
    }

    if (this.isTypingVisible) {
      this.chatWindow?.hideTypingIndicator();
      this.isTypingVisible = false;
    }
  }
}
